import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { DEFAULT_QUERY_STALE_TIME } from "@/queries/queryUtil";
import { getPbxClient } from "@/lib/apiClient";
import type { SsoProvider } from "@/queries/useLoginConfigQuery";

/**
 * CT-57 — the invitee-facing accept page, which had been left behind.
 *
 * Invite CREATION moved to pbx-api (POST /api/invitations), but this page still validated against
 * Laravel's `/invitation/check/:token` — a route pbx-api does not serve. So every invitation the
 * admin screen now sends produced a link that 404'd with `{"ok":false,"error":"not_found"}` on
 * arrival: unacceptable invitations, and no way for a new member to join.
 *
 * WHICH BACKEND IS CHOSEN BY THE TOKEN'S SHAPE, not by a fallback attempt. pbx-api mints
 * `it_` + 64 hex (invitations/token.ts, 256 random bits); Laravel's look nothing like it. That keeps
 * invitations already sitting in someone's inbox working, without a speculative request that would
 * make a real "expired" answer indistinguishable from "wrong backend".
 */
const PBX_API_TOKEN_RE = /^it_[a-f0-9]{64}$/;

export function isPbxApiInviteToken(token: string): boolean {
    return PBX_API_TOKEN_RE.test(token);
}

type PbxApiInvitation = {
    email?: string;
    tenant_name?: string;
    inviter_name?: string;
    sso?: { providers?: string[]; forced?: boolean };
};

/** The union both backends are normalized into, so the form does not branch. */
export type ValidateInvitationReponse = {
    valid: boolean;
    /** On failure: the error code the login page shows. On success: the invitee's email. */
    data: string;
    /** Laravel sends enabled/hide_credentials; pbx-api (CT-558) also names the providers. */
    sso?: { enabled?: boolean; hide_credentials?: boolean; providers?: SsoProvider[] };
    tenant_name?: string;
    inviter_name?: string;
};

/** CT-558 — pbx-api's invitation → the shape the form reads. Unknown providers are dropped. */
export function toPbxApiInvitation(invitation: PbxApiInvitation | undefined): ValidateInvitationReponse {
    const providers = (invitation?.sso?.providers ?? []).filter(
        (provider): provider is SsoProvider => provider === "google" || provider === "microsoft"
    );
    return {
        valid: true,
        data: invitation?.email ?? "",
        tenant_name: invitation?.tenant_name,
        inviter_name: invitation?.inviter_name,
        sso: {
            enabled: providers.length > 0,
            hide_credentials: Boolean(invitation?.sso?.forced) && providers.length > 0,
            providers,
        },
    };
}

async function fetchFromPbxApi(token: string): Promise<ValidateInvitationReponse> {
    try {
        const { data } = await getPbxClient().get(`/invitations/${encodeURIComponent(token)}`);
        return toPbxApiInvitation(data.invitation);
    } catch (error: any) {
        // The API distinguishes invalid_token / expired / revoked / already_accepted; the login
        // page words whichever it is. No usable body (network error, bare 5xx) must NOT claim the
        // invitation is gone — internal_error words as "try again" (CT-33).
        return { valid: false, data: error?.response?.data?.error ?? "internal_error" };
    }
}

async function fetchFromLaravel(token: string): Promise<ValidateInvitationReponse> {
    const { data } = await getPbxClient().get(`/invitation/check/${encodeURIComponent(token)}`);
    return data;
}

export function useInvitationQuery(token: string): UseQueryResult<ValidateInvitationReponse> {
    return useQuery({
        queryKey: ["invitation", token],
        queryFn: async () => (isPbxApiInviteToken(token) ? fetchFromPbxApi(token) : fetchFromLaravel(token)),
        staleTime: DEFAULT_QUERY_STALE_TIME,
        retry: 0,
        refetchOnWindowFocus: false,
    });
}
